import camelCase from "lodash/camelCase";
import React, { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Switch } from "../../../utils/local-antd";

import { Flex } from "../../../components/Flex";
import { Text } from "../../../components/Text";
import { UpdateUserSettingsDto } from "../../../model/user/settings/dto/UpdateUserSettingsDto";
import { NotificationsCases } from "../../../model/user/settings/types/notifications/NotificationsCases.enum";
import { NotificationsEntities } from "../../../model/user/settings/types/notifications/NotificationsEntities.enum";
import { NotificationsTypes } from "../../../model/user/settings/types/notifications/NotificationsTypes.enum";
import { Spacing } from "../../../theme/Spacing";
import { Button } from "../../../components/Button";
import { AtiraToast } from "../../../utils/AtiraToast";
import { SettingsWrapper } from "../components/SettingsWrapper";
import { AtiraIcon } from "../../../components/AtiraIcon";
import { EntityIcons } from "../../../utils/GlobalIcons";
import { Info } from "../../../components/Info";
import { faEnvelopeOpenText } from "@fortawesome/free-solid-svg-icons/faEnvelopeOpenText";
import { faMobileAlt } from "@fortawesome/free-solid-svg-icons/faMobileAlt";
import { TestIDs } from "../../../tests/types/testIds";

const SaveButton = styled(Button)`
  margin: 0;
  width: fit-content;
  margin-top: ${Spacing.m};
`;

type EntityIcon = (typeof EntityIcons)[keyof typeof EntityIcons];

const ENTITY_TO_ICON: Record<NotificationsEntities, EntityIcon> = {
  [NotificationsEntities.ENTRY]: EntityIcons.Entries,
  [NotificationsEntities.ORPHAN_TASK]: EntityIcons.Tasks,
  [NotificationsEntities.LEAD_TASK]: EntityIcons.Tasks,
  [NotificationsEntities.CONTACT_TASK]: EntityIcons.Tasks,
  [NotificationsEntities.KANBAN_TASK]: EntityIcons.Tasks,
  [NotificationsEntities.FORM]: EntityIcons.Forms,
};

type SettingsNotificationsProps = {
  onSave: VoidFunction;
};

export const SettingsNotifications: React.FC<SettingsNotificationsProps> = ({
  onSave,
}) => {
  const [loading, setLoading] = useState(false);

  const { t } = useTranslation();
  const { control } = useFormContext<UpdateUserSettingsDto>();

  const entityCases: Record<NotificationsEntities, NotificationsCases[]> = {
    [NotificationsEntities.ENTRY]: [NotificationsCases.ON_NEW],
    [NotificationsEntities.ORPHAN_TASK]: [NotificationsCases.ON_ASSIGN],
    [NotificationsEntities.LEAD_TASK]: [
      NotificationsCases.ON_ASSIGN,
      NotificationsCases.ON_AUTO_REMINDER,
    ],

    [NotificationsEntities.CONTACT_TASK]: [NotificationsCases.ON_ASSIGN],
    [NotificationsEntities.KANBAN_TASK]: [NotificationsCases.ON_ASSIGN],
    [NotificationsEntities.FORM]: [NotificationsCases.ON_ASSIGN],
  };

  const localeOnSave = async () => {
    try {
      setLoading(true);
      await onSave();
    } catch (e: any) {
      AtiraToast.apiError(e);
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <SettingsWrapper>
      <Info info={t("settings.notifications.description")} />

      <Flex flexDirection="column" gap="l" marginTop="m">
        {Object.entries(entityCases).map(([entityKey, cases]) => (
          <Flex key={`${entityKey}`} flexDirection="column" gap="m">
            {cases.map((caseKey) => (
              <Flex key={`${entityKey}-${caseKey}`} gap="s">
                <Flex flex={2} alignItems="center" gap="s">
                  <AtiraIcon
                    icon={ENTITY_TO_ICON[entityKey as NotificationsEntities]}
                    color="main"
                  />
                  <Text>
                    {t(
                      `settings.notifications.${camelCase(entityKey)}.${camelCase(caseKey)}`,
                    )}
                  </Text>
                </Flex>

                <Flex gap="xxxl" flex={4} justifyContent="flex-end">
                  {Object.values(NotificationsTypes).map((typeKey) => (
                    <Flex
                      key={`${entityKey}-${caseKey}-${typeKey}`}
                      flexDirection="row"
                      alignItems="center"
                      gap="s"
                    >
                      <Flex alignItems="center" gap="xs">
                        <AtiraIcon
                          color="main"
                          icon={
                            typeKey === NotificationsTypes.EMAIL
                              ? faEnvelopeOpenText
                              : faMobileAlt
                          }
                        />
                        <Text fontSize="s">
                          {t(
                            `settings.notifications.types.${camelCase(typeKey)}`,
                          )}
                        </Text>
                      </Flex>
                      <Controller
                        control={control}
                        name={
                          `notifications.${entityKey}.${caseKey}.${typeKey}` as any
                        }
                        render={({ field: { value, onChange } }) => (
                          <Switch
                            checked={value}
                            onChange={onChange}
                            data-testid={TestIDs.general.settings.notifications.toggle(
                              entityKey,
                              caseKey,
                              typeKey,
                            )}
                          />
                        )}
                      />
                    </Flex>
                  ))}
                </Flex>
              </Flex>
            ))}
          </Flex>
        ))}
      </Flex>

      <SaveButton
        dataTestId={TestIDs.general.settings.notifications.button}
        onClick={localeOnSave}
        loading={loading}
      >
        {t("common.save_changes")}
      </SaveButton>
    </SettingsWrapper>
  );
};
